import {
  faBolt,
  faBoxCheck,
  faBuilding,
  faClipboardListCheck,
  faCoins,
  faEarthAmericas,
  faEngineWarning,
  faKeySkeleton,
  faShareNodes,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { PageSkeleton } from '@/components/shared/PageSkeleton';
import PageTabs from '@/components/shared/PageTabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useFacilityProcessOptions } from '@/hooks/useDropdowns';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { CompanyActionDialog } from '../../-components/CompanyActionDialog';
import { CompanyDetailHeader } from '../../-components/CompanyDetailHeader';
import { CompanyMergeProgress } from '../../-components/CompanyMergeProgress';
import { SetParentCompanyDialog } from '../../-components/SetParentCompanyDialog';
import {
  adminCompanyQueryOptions,
  useAdminCompany,
} from '../../-hooks/useAdminCompany';
import { useAdminCompanyDetailActions } from '../../-hooks/useAdminCompanyDetailActions';
import { useCompanyMerges } from '../../-hooks/useCompanyMerges';
import { useParentCompanyName } from '../../-hooks/useParentCompanyName';
import { useSetParentCompanyAction } from '../../-hooks/useSetParentCompanyAction';

const companyDetailSearch = z.object({
  // Which roster the operator came from — a removed company is only readable
  // through its own endpoint, so this has to survive a reload.
  isRemoved: fallback(z.boolean(), false),
  /** Which profile template the General Information tab has open. */
  genTemplate: z.enum(['ABOUT', 'OTHER_IDS', 'OPERATIONAL_METRICS']).optional(),
});

export const Route = createFileRoute(
  '/management/companies/(pages)/$companyId',
)({
  validateSearch: companyDetailSearch,
  loaderDeps: ({ search }) => ({ isRemoved: search.isRemoved ?? false }),
  loader: ({ params, deps }) =>
    queryClient
      .ensureQueryData(
        adminCompanyQueryOptions(params.companyId, deps.isRemoved),
      )
      // A failed prefetch must not blank the tab — the component's own query
      // re-runs and surfaces the error inline.
      .catch(() => undefined),
  component: CompanyDetailLayout,
});

function CompanyDetailLayout() {
  const { companyId } = Route.useParams();
  const { isRemoved } = Route.useSearch();
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const { company, isLoading, isError } = useAdminCompany(companyId, isRemoved);
  const actions = useAdminCompanyDetailActions(companyId);
  const setParentAction = useSetParentCompanyAction(companyId);
  const parentName = useParentCompanyName(company?.parentId);
  const merges = useCompanyMerges(companyId);
  const navigate = useNavigate();
  const { data: facilityProcesses = [] } = useFacilityProcessOptions();

  if (isLoading) return <PageSkeleton />;

  if (isError || !company) {
    return (
      <PageContainer>
        <Alert variant='destructive' className='py-2.5'>
          <AlertDescription>{t('errors.detailFailed')}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const facilityProcessLabels = (company.facilityProcesses ?? '')
    .split(',')
    .map(key => key.trim())
    .filter(key => key !== '')
    .map(key => facilityProcesses.find(o => o.key === key)?.label ?? key);

  return (
    // Scrolls as a whole, with the tab row sticking — the mixed-layout split, so
    // table tabs can still fill their own height while card tabs overflow.
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <CompanyDetailHeader
        company={company}
        isRemoved={isRemoved}
        facilityProcessLabels={facilityProcessLabels}
        onAction={actions.ask}
        isActionPending={actions.isPending}
        parentName={parentName}
        onSetParent={() => setParentAction.open(company.parentId)}
        onMerge={() =>
          void navigate({
            to: '/management/company-merges/new',
            search: { source: companyId },
          })
        }
        isMerging={merges.isActive}
      />

      {merges.latest && (
        <CompanyMergeProgress
          merge={merges.latest}
          companyId={companyId}
          onResume={() => void merges.resume()}
          isResuming={merges.isResuming}
        />
      )}

      {/* Sticky under the header, exactly as the customer company page pins it.
          `PageTabs` keeps its own `pt-4` / `pt-6`: the tab row's height is what
          the profile toolbar's `top-14` / `top-17` offset is measured against,
          so trimming it here unpins the search bar and opens a gap under it. */}
      <div className='sticky top-0 z-20 bg-background'>
        <PageTabs
          mode='path'
          tabs={[
            // Same order and the same icons the old tab row had.
            {
              key: 'general-info',
              label: t('detail.tabs.generalInfo'),
              icon: <FontAwesomeIcon icon={faEarthAmericas} />,
              to: `/management/companies/${companyId}/general-info`,
            },
            {
              key: 'platform-features',
              label: t('detail.tabs.platformFeatures'),
              icon: <FontAwesomeIcon icon={faClipboardListCheck} />,
              to: `/management/companies/${companyId}/platform-features`,
            },
            {
              key: 'network',
              label: t('detail.tabs.network'),
              icon: <FontAwesomeIcon icon={faBuilding} />,
              to: `/management/companies/${companyId}/network`,
            },
            {
              key: 'tech-insights',
              label: t('detail.tabs.techInsights'),
              icon: <FontAwesomeIcon icon={faEngineWarning} />,
              to: `/management/companies/${companyId}/tech-insights`,
            },
            {
              key: 'tracing-orders',
              label: t('detail.tabs.tracingOrders'),
              icon: <FontAwesomeIcon icon={faBoxCheck} />,
              to: `/management/companies/${companyId}/tracing-orders`,
            },
            {
              key: 'employees',
              label: t('detail.tabs.employees'),
              icon: <FontAwesomeIcon icon={faUsers} />,
              to: `/management/companies/${companyId}/employees`,
            },
            {
              key: 'supply-chains',
              label: t('detail.tabs.supplyChains'),
              icon: <FontAwesomeIcon icon={faShareNodes} />,
              to: `/management/companies/${companyId}/supply-chains`,
            },
            {
              key: 'api-keys',
              label: t('detail.tabs.apiKeys'),
              icon: <FontAwesomeIcon icon={faKeySkeleton} />,
              to: `/management/companies/${companyId}/api-keys`,
            },
            {
              key: 'credits',
              label: t('detail.tabs.credits'),
              icon: <FontAwesomeIcon icon={faCoins} />,
              to: `/management/companies/${companyId}/credits`,
            },
            {
              key: 'automation-rules',
              label: t('detail.tabs.automationRules'),
              icon: <FontAwesomeIcon icon={faBolt} />,
              to: `/management/companies/${companyId}/automation-rules`,
            },
          ]}
          moreLabel={tShared('moreTabs')}
        />
      </div>

      {/* The floor makes short viewports scroll the page (header away, tabs
          sticky) instead of squeezing a table tab into the leftover under the
          header — mirrors the customer company page. The wrapper carries no
          overflow-*: tab content pins its own toolbar against this scroll
          container, and an overflowing wrapper would become the scrollport
          and unpin it. */}
      <div className='flex flex-col flex-1 min-h-[70dvh]'>
        <Outlet />
      </div>

      <CompanyActionDialog
        action={actions.pending}
        companyName={company.name ?? company.officialName ?? company.id}
        onCancel={actions.cancel}
        onConfirm={() => void actions.confirm()}
        isPending={actions.isPending}
      />

      <SetParentCompanyDialog
        action={setParentAction}
        currentParentName={parentName}
      />
    </div>
  );
}
